import { useMemo } from "react";
import {
  useAudienceExploreDependenciesQuery,
  useParentModelForQueryBuilderQuery,
} from "src/graphql";
import {
  DestinationRules,
  FullParentModel,
} from "src/components/audiences/types";
import {
  collectFilterableSegmentIdsNeedingFetch,
  hydrateParentModelWithFilterableColumns,
} from "src/hooks/hydrate-filterable-audience-columns";
import { useFetchFilterableAudienceColumns } from "src/hooks/use-fetch-filterable-audience-columns";

export const useFetchQueryBuilderParentModel = ({
  parentModelId,
}: {
  parentModelId: string | undefined;
}): {
  parentModel: (FullParentModel & DestinationRules) | null;
  isLoading: boolean;
} => {
  const { data: parentModelFields, isLoading: isParentModelFieldsLoading } =
    useParentModelForQueryBuilderQuery(
      { parentModelId: parentModelId?.toString() ?? "" },
      {
        enabled: Boolean(parentModelId),
        select: (data) => data.segments_by_pk,
      },
    );

  const {
    data: parentModelDependencies,
    isLoading: isParentModelDependenciesLoading,
  } = useAudienceExploreDependenciesQuery(
    { parentModelId: parentModelId?.toString() ?? "" },
    { enabled: Boolean(parentModelId), select: (data) => data.segments_by_pk },
  );

  const mergedParent = useMemo(() => {
    if (!parentModelFields || !parentModelDependencies) {
      return null;
    }

    return {
      ...parentModelFields,
      ...parentModelDependencies,
    };
  }, [parentModelFields, parentModelDependencies]);

  const filterableSegmentIds = useMemo(
    () => collectFilterableSegmentIdsNeedingFetch(mergedParent),
    [mergedParent],
  );

  const {
    filterableColumns: filterableColumnsByModelId,
    isLoading: isFilterableColumnsLoading,
  } = useFetchFilterableAudienceColumns(filterableSegmentIds, {
    enabled:
      Boolean(parentModelId && mergedParent) && filterableSegmentIds.length > 0,
  });

  const parentModel = useMemo(
    () =>
      mergedParent
        ? hydrateParentModelWithFilterableColumns(
            mergedParent,
            filterableColumnsByModelId,
          )
        : null,
    [mergedParent, filterableColumnsByModelId],
  );

  return {
    parentModel: parentModel ?? null,
    isLoading:
      isParentModelFieldsLoading ||
      isParentModelDependenciesLoading ||
      isFilterableColumnsLoading,
  };
};
